import { Component, Input, OnInit } from '@angular/core';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { AccountService } from '../../../../../../../../../services/account/account.service';
import { CustomerService } from '../../../../../../../../../services/customer/customer.service';
import { openClimberAppInNewTabAsCustomer } from '../../../../../../../../../utils-models';
import { getAge } from '../../../../../../../../../utils';
import { OrganizationConfigService } from '../../../../../../../../../services/organization-config/organization-config.service';
import { Customer } from '../../../../../../../../../models/customer';
import { Membership } from '../../../../../../../../../models/membership';
import { MembershipService } from '../../../../../../../../../services/membership/membership.service';
import { MembershipPurchaseType, MembershipStatus } from '../../../../../../../../../models/membership-enums';

@Component({
  selector: 'app-purchase-customer-membership-modal',
  templateUrl: './purchase-customer-membership-modal.component.html',
  styleUrls: ['./purchase-customer-membership-modal.component.css']
})
export class PurchaseCustomerMembershipModalComponent implements OnInit {

  @Input() customer: Customer;

  public membershipId: number = null;

  public membershipList: Membership[];

  public loadingMemberships = false;
  public loadingSubmit = false;

  public serverError: number = null;

  public errors: any = {};
  public errorMessage = '';

  public MembershipStatus = MembershipStatus;

  constructor(
    private accountService: AccountService,
    private membershipService: MembershipService,
    private customerService: CustomerService,
    private orgService: OrganizationConfigService,
    public activeModal: NgbActiveModal
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().can_manage_members) {
      return;
    }

    this.loadMemberships();
    this.checkAgeRestriction();
  }

  public onMembershipChange() {
    this.checkAgeRestriction();
  }

  public isAgeBlocked(): boolean {
    const orgConfig = this.orgService.getOrganizationConfig();
    if (!orgConfig.membership_minimum_age || !this.customer?.birthday) { return false; }
    const isUnderage = getAge(this.customer.birthday) < orgConfig.membership_minimum_age;
    if (!isUnderage) { return false; }
    if (orgConfig.membership_minimum_age_applies_to_prepaid) { return true; }
    const selected = this.membershipList?.find(m => m.id === Number(this.membershipId));
    return selected?.purchase_type === MembershipPurchaseType.recurring;
  }

  public handleSave() {
    this.loadingSubmit = true;
    this.errors = {};
    this.errorMessage = '';

    this.customerService
      .getCustomerClimberAppToken(this.customer.id)
      .then(token => {
        const baseUrl = this.orgService.getOrganizationConfig().climber_app_base_url;
        openClimberAppInNewTabAsCustomer(
          token,
          this.customer.id,
          baseUrl,
          baseUrl + 'membership/choose-members/' + this.membershipId.toString() + '/'
        );
        this.activeModal.close();
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to purchase memberships for this customer.';
        } else if (reason.status === 400) {
          this.errorMessage = 'Please correct the errors above and try again.';
          this.errors = reason.error;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }

  private checkAgeRestriction() {
    if (!this.isAgeBlocked()) {
      this.errorMessage = '';
      return;
    }
    const orgConfig = this.orgService.getOrganizationConfig();
    if (orgConfig.membership_minimum_age_applies_to_prepaid) {
      this.errorMessage = `This customer is too young to own a membership. ` +
        `They must be at least ${orgConfig.membership_minimum_age} years old. ` +
        `Please link this customer to an account of sufficient age and make the purchase from that account.`;
    } else {
      this.errorMessage = `This customer is too young to own a recurring membership. ` +
        `They must be at least ${orgConfig.membership_minimum_age} years old. ` +
        `Please link this customer to an account of sufficient age and make the purchase from that account. ` +
        `If a prepaid membership is selected, this restriction does not apply.`;
    }
  }

  private loadMemberships() {
    this.loadingMemberships = true;

    this.membershipService
      .getMemberships(
        1,
        100,
        {status: MembershipStatus.active}
      )
      .then(response => {
        this.membershipList = response.results.filter(m => (m.purchase_type !== MembershipPurchaseType.benefit));
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingMemberships = false; });
  }

}
